/**
 * Queue offline-first des mutations API — Phase 3.3.1.
 *
 * Wrapper IDB autour du store `mutations` (cf. `db.ts`). Pattern
 * **outbox** classique : quand une mutation HTTP échoue par perte
 * réseau (ou si on est offline au moment d'émettre), on l'enqueue
 * pour replay au prochain `online` event.
 *
 * Garanties :
 *
 *   - **Persistance reload** : tout passe par IDB, pas de cache
 *     mémoire — l'utilisateur peut fermer l'onglet, le replay
 *     reprendra au prochain démarrage.
 *   - **FIFO** : le keyPath auto-increment de IDB garantit l'ordre
 *     d'insertion. Important quand 2 mutations dépendent (ex.
 *     créer un event puis le PATCH).
 *   - **Tolérance IDB absent** (Safari privacy mode, environnement
 *     test sans shim) : tous les helpers retournent un fallback
 *     gracieux (`false` / `[]`). Le caller observe et peut décider
 *     de drop la mutation (le user perd ses changements offline,
 *     mais l'app ne crash pas).
 *
 * Pas d'idempotency-key en 3.3.1 — différé à 3.3.2+ si on observe
 * des doublons. Pour les sub-entités texte (events/tasks/…), le
 * risque est faible : un user qui POST une tâche puis perd le
 * réseau a peu de chance de re-POSTer la même seconde plus tard.
 */
import { openDb, type MutationEntry, type MutationMedia } from './db.js';

/**
 * Enqueue une mutation. Retourne le `seq` attribué par IDB (ou
 * `null` si IDB indisponible — le caller décide quoi faire).
 */
export async function enqueueMutation(
  mutation: Omit<MutationEntry, 'seq' | 'createdAt' | 'attempts'>,
): Promise<number | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    const entry: Omit<MutationEntry, 'seq'> = {
      method: mutation.method,
      path: mutation.path,
      body: mutation.body,
      createdAt: Date.now(),
      attempts: 0,
      ...(mutation.media ? { media: mutation.media } : {}),
      ...(mutation.localId ? { localId: mutation.localId } : {}),
    };
    // `add` retourne la clé attribuée (le `seq` auto-incrémenté).
    const seq = (await db.add('mutations', entry as MutationEntry)) as number;
    return seq;
  } catch (err) {
    console.warn('[mutations-queue] enqueue failed:', err);
    return null;
  }
}

/**
 * Enqueue une mutation d'upload binaire — Phase 3.3.5, étendue
 * 3.3.7 avec `localId` optionnel pour le remap.
 *
 * Le blob source doit déjà avoir été persisté en IDB sous
 * `media.blobKey` (typiquement `voice:<id>` ou `photo:<id>`) avant
 * l'appel.
 *
 * `method` et `path` du shape `MutationEntry` ne sont pas utilisés
 * pour les media mutations — le replay engine route vers le
 * flow 3-step via `media.uploadUrlPath` + `media.confirmPath`. On
 * stocke quand même `path` = `confirmPath` pour le diagnostic
 * et pour le path-based dispatch du broker remap.
 *
 * `localId` (3.3.7) : si fourni, propagé au `MutationEntry` ; au
 * replay success, l'id serveur extrait de la réponse confirm
 * déclenche un event `nestor:mutation-remap`.
 */
export async function enqueueMediaMutation(
  media: MutationMedia,
  localId?: string,
): Promise<number | null> {
  return enqueueMutation({
    method: 'POST',
    path: media.confirmPath,
    body: null,
    media,
    ...(localId ? { localId } : {}),
  });
}

/**
 * Liste les mutations en attente, dans l'ordre FIFO (par `seq`
 * croissant). Vide si IDB indisponible.
 */
export async function listPendingMutations(): Promise<MutationEntry[]> {
  const db = await openDb();
  if (!db) return [];
  try {
    // `getAll` sur un store avec keyPath autoIncrement retourne
    // les entrées dans l'ordre des clés (= ordre d'insertion).
    return await db.getAll('mutations');
  } catch (err) {
    console.warn('[mutations-queue] list failed:', err);
    return [];
  }
}

/**
 * Retire une mutation de la queue par son `seq`. Idempotent (pas
 * d'erreur si déjà supprimée). Retourne `true` si la mutation a
 * été retirée avec succès.
 */
export async function removeMutation(seq: number): Promise<boolean> {
  const db = await openDb();
  if (!db) return false;
  try {
    await db.delete('mutations', seq);
    return true;
  } catch (err) {
    console.warn('[mutations-queue] remove failed:', err);
    return false;
  }
}

/**
 * Incrémente le compteur d'attempts d'une mutation. Utilisé par
 * la replay engine quand un retry échoue mais qu'on veut garder
 * la mutation en queue (vs la drop). Retourne le nouveau compteur
 * ou `null` si la mutation n'existe plus / IDB KO.
 */
export async function incrementMutationAttempts(seq: number): Promise<number | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    const tx = db.transaction('mutations', 'readwrite');
    const store = tx.objectStore('mutations');
    const existing = await store.get(seq);
    if (!existing) {
      await tx.done;
      return null;
    }
    const next: MutationEntry = { ...existing, attempts: existing.attempts + 1 };
    await store.put(next);
    await tx.done;
    return next.attempts;
  } catch (err) {
    console.warn('[mutations-queue] incrementAttempts failed:', err);
    return null;
  }
}

/**
 * Compte les mutations en attente — utile pour un badge UI
 * « X mutations en attente » (différé à 3.3.2+).
 */
export async function countPendingMutations(): Promise<number> {
  const db = await openDb();
  if (!db) return 0;
  try {
    return await db.count('mutations');
  } catch {
    return 0;
  }
}

/**
 * Vide entièrement la queue. **À utiliser avec précaution** — perd
 * toutes les mutations en attente. Cas d'usage : logout (les
 * mutations en queue référencent une session qui n'est plus valide).
 */
export async function clearMutationsQueue(): Promise<boolean> {
  const db = await openDb();
  if (!db) return false;
  try {
    await db.clear('mutations');
    return true;
  } catch (err) {
    console.warn('[mutations-queue] clear failed:', err);
    return false;
  }
}
